import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput, Textarea, Select } from 'flowbite-react';
import * as wasm from 'sched';
import { v4 as uuidv4 } from 'uuid';

const NativeProductSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  lifecycleStage: z.string().optional(),
});

type NativeProductFormValues = z.infer<typeof NativeProductSchema>;

interface NativeProductFormProps {
  onSubmit: (command: wasm.DatabaseCommand) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
}

const LIFECYCLE_STAGES = [
  { value: '', label: 'None' },
  { value: 'Concept', label: 'Concept' },
  { value: 'Development', label: 'Development' },
  { value: 'Growth', label: 'Growth' },
  { value: 'Mature', label: 'Mature' },
  { value: 'Sunset', label: 'Sunset' },
];

const NativeProductForm: React.FC<NativeProductFormProps> = ({ onSubmit, onClose, isOpen }) => {
  if (!isOpen) return null;

  const handleSubmit = async (values: NativeProductFormValues) => {
    const id = uuidv4();
    const command: wasm.DatabaseCommand = {
      type: 'CreateItem',
      id,
      item: {
        type: 'Product',
        value: {
          name: values.name,
          description: values.description || null,
          reference: null,
          draft: false,
          lifecycle_stage: values.lifecycleStage || null,
          created_at: new Date().toISOString(),
        },
      },
    };
    await onSubmit(command);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-cm-bg-surface p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4 text-cm-text-primary">Create Product</h2>

        <Formik
          initialValues={{ name: '', description: '', lifecycleStage: '' }}
          validationSchema={toFormikValidationSchema(NativeProductSchema)}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <div className="mb-2">
                  <Label htmlFor="name">Name</Label>
                </div>
                <Field
                  as={TextInput}
                  id="name"
                  name="name"
                  autoFocus
                  placeholder="e.g. Chronomap, Internal Tools"
                />
                <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <div className="mb-2">
                  <Label htmlFor="description">Description</Label>
                </div>
                <Field
                  as={Textarea}
                  id="description"
                  name="description"
                  placeholder="What is this product?"
                  rows={3}
                />
              </div>

              <div>
                <div className="mb-2">
                  <Label htmlFor="lifecycleStage">Lifecycle Stage</Label>
                </div>
                <Field as={Select} id="lifecycleStage" name="lifecycleStage">
                  {LIFECYCLE_STAGES.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </Field>
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button color="gray" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={isSubmitting}>Create Product</Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default NativeProductForm;
